import React, { useEffect, useState } from 'react';
import { Star, Eye, EyeOff, Edit, Trash2, X, Save, RefreshCw } from 'lucide-react';
import { notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_PILL = {
  published: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  draft:     'bg-amber-500/15 text-amber-700 border-amber-500/30',
  archived:  'bg-red-500/15 text-red-700 border-red-500/30',
};

const CATEGORIES = [
  { id: 'visas', label: 'Visas' },
  { id: 'housing', label: 'Housing' },
  { id: 'banking', label: 'Banking' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'city-guides', label: 'City Guides' },
  { id: 'community', label: 'Community' },
  { id: 'migration-stats', label: 'Migration Stats' },
];

/**
 * AdminNewsEditor - Admin UI to edit Claude-generated articles.
 * Live at /admin/dashboard → Overview → "Article Library" panel.
 *
 * Features:
 *   - List ALL articles (published, draft, archived)
 *   - Inline toggle: featured · publish/unpublish · archive
 *   - Edit modal: title, excerpt, body markdown, category, hero image, SEO fields
 */
const AdminNewsEditor = () => {
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // article being edited
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/news/all`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setArticles(data.articles || []);
    } catch (e) {
      setError(String(e.message || e));
      setArticles([]);
    }
  };

  useEffect(() => { load(); }, []);

  const patchArticle = async (slug, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/news/${encodeURIComponent(slug)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      await load();
      return true;
    } catch (e) {
      notifyError(`Update failed: ${e.message || e}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (a) => {
    const next = a.status === 'published' ? 'draft' : 'published';
    await patchArticle(a.slug, { status: next });
  };

  const toggleFeatured = async (a) => {
    await patchArticle(a.slug, { featured: !a.featured });
  };

  const archive = async (a) => {
    if (!window.confirm(`Archive "${a.title}"? It will be hidden from the public feed.`)) return;
    await patchArticle(a.slug, { status: 'archived' });
  };

  return (
    <div
      className="bg-[#FFFFFF] border border-[#F4ECD8] p-6"
      data-testid="admin-news-editor"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-[#0A1F2E]">Article Library</h3>
          <p className="text-xs text-[#5A6B73] mt-1">
            Edit body, swap hero, toggle featured, publish/unpublish.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-[0.12em] font-semibold border border-[#0A1F2E]/20 px-3 py-1.5 hover:bg-[#0A1F2E] hover:text-[#F9F6EF] transition rounded-full"
          data-testid="admin-news-editor-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          Failed to load: {error}
        </div>
      )}

      {articles === null ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 bg-[#F4ECD8] animate-pulse rounded" />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <p className="text-sm text-[#5A6B73]">No articles yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#F4ECD8] text-xs text-[#5A6B73] uppercase tracking-wider">
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Featured</th>
                <th className="py-2 pr-3">Updated</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.slug} className="border-b border-[#F4ECD8]/60 hover:bg-[#F9F6EF]/40">
                  <td className="py-3 pr-3 max-w-md">
                    <div className="font-display italic text-[#0A1F2E] truncate">{a.title}</div>
                    <div className="text-xs text-[#5A6B73] truncate font-mono">{a.slug}</div>
                  </td>
                  <td className="py-3 pr-3 text-xs">{a.category_label}</td>
                  <td className="py-3 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${STATUS_PILL[a.status] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                      {a.status || 'unknown'}
                    </span>
                  </td>
                  <td className="py-3 pr-3">
                    <button
                      type="button"
                      onClick={() => toggleFeatured(a)}
                      disabled={busy}
                      title={a.featured ? 'Unfeature' : 'Mark featured'}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded transition text-xs ${
                        a.featured ? 'bg-[#E8A830] text-[#0A1F2E]' : 'border border-[#0A1F2E]/15 text-[#0A1F2E]/55'
                      }`}
                      data-testid={`admin-news-feature-${a.slug}`}
                    >
                      <Star className={`w-3 h-3 ${a.featured ? 'fill-current' : ''}`} />
                      {a.featured ? 'Featured' : 'Off'}
                    </button>
                  </td>
                  <td className="py-3 pr-3 text-xs text-[#5A6B73]">
                    {a.published_at ? new Date(a.published_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}
                  </td>
                  <td className="py-3 pr-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(a)}
                        disabled={busy}
                        className="p-1.5 hover:bg-[#0A1F2E]/5 rounded text-[#1A5C6E]"
                        title="Edit"
                        data-testid={`admin-news-edit-${a.slug}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePublish(a)}
                        disabled={busy}
                        className="p-1.5 hover:bg-[#0A1F2E]/5 rounded text-[#1A5C6E]"
                        title={a.status === 'published' ? 'Unpublish' : 'Publish'}
                        data-testid={`admin-news-publish-${a.slug}`}
                      >
                        {a.status === 'published'
                          ? <EyeOff className="w-4 h-4" />
                          : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => archive(a)}
                        disabled={busy}
                        className="p-1.5 hover:bg-red-50 rounded text-red-600"
                        title="Archive"
                        data-testid={`admin-news-archive-${a.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditorModal
          article={editing}
          onClose={() => setEditing(null)}
          onSaved={async (changes) => {
            const ok = await patchArticle(editing.slug, changes);
            if (ok) setEditing(null);
          }}
          busy={busy}
        />
      )}
    </div>
  );
};

// ============== EDIT MODAL ==============
const EditorModal = ({ article, onClose, onSaved, busy }) => {
  const [form, setForm] = useState({
    title: article.title || '',
    excerpt: article.excerpt || '',
    category: article.category || 'visas',
    hero_image_url: article.hero_image_url || '',
    seo_title: article.seo_title || '',
    seo_description: article.seo_description || '',
    body_markdown: article.body_markdown || '',
    featured: !!article.featured,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="admin-news-edit-modal"
    >
      <div className="bg-white max-w-4xl w-full my-8 rounded shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F4ECD8] sticky top-0 bg-white">
          <h3 className="font-display italic text-2xl text-[#0A1F2E]">Edit article</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-[#F4ECD8] rounded"
            data-testid="admin-news-edit-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={set('title')}
              className="w-full px-3 py-2 border border-[#F4ECD8] rounded font-display italic text-xl text-[#0A1F2E] focus:outline-none focus:border-[#2CA8A0]"
              data-testid="admin-news-edit-title"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                Category
              </label>
              <select
                value={form.category}
                onChange={set('category')}
                className="w-full px-3 py-2 border border-[#F4ECD8] rounded text-sm focus:outline-none focus:border-[#2CA8A0]"
                data-testid="admin-news-edit-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={setBool('featured')}
                  className="w-4 h-4"
                  data-testid="admin-news-edit-featured"
                />
                Featured (center hero on /relo-news)
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
              Hero image URL
            </label>
            <input
              type="url"
              value={form.hero_image_url}
              onChange={set('hero_image_url')}
              placeholder="https://images.unsplash.com/..."
              className="w-full px-3 py-2 border border-[#F4ECD8] rounded text-sm font-mono focus:outline-none focus:border-[#2CA8A0]"
              data-testid="admin-news-edit-hero"
            />
            {form.hero_image_url && (
              <div className="mt-2 aspect-video bg-[#F4ECD8] overflow-hidden">
                <img
                  src={form.hero_image_url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
              Excerpt
            </label>
            <textarea
              value={form.excerpt}
              onChange={set('excerpt')}
              rows={2}
              className="w-full px-3 py-2 border border-[#F4ECD8] rounded text-sm focus:outline-none focus:border-[#2CA8A0]"
              data-testid="admin-news-edit-excerpt"
            />
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
              Body (Markdown)
            </label>
            <textarea
              value={form.body_markdown}
              onChange={set('body_markdown')}
              rows={16}
              className="w-full px-3 py-2 border border-[#F4ECD8] rounded text-sm font-mono leading-relaxed focus:outline-none focus:border-[#2CA8A0]"
              data-testid="admin-news-edit-body"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                SEO title
              </label>
              <input
                type="text"
                value={form.seo_title}
                onChange={set('seo_title')}
                className="w-full px-3 py-2 border border-[#F4ECD8] rounded text-sm focus:outline-none focus:border-[#2CA8A0]"
                data-testid="admin-news-edit-seo-title"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-[#5A6B73] mb-1">
                SEO description
              </label>
              <input
                type="text"
                value={form.seo_description}
                onChange={set('seo_description')}
                className="w-full px-3 py-2 border border-[#F4ECD8] rounded text-sm focus:outline-none focus:border-[#2CA8A0]"
                data-testid="admin-news-edit-seo-desc"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#F4ECD8] sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#0A1F2E]/15 rounded hover:bg-[#F4ECD8] transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSaved(form)}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-[#0A1F2E] text-[#F9F6EF] rounded hover:brightness-110 disabled:opacity-50 transition"
            data-testid="admin-news-edit-save"
          >
            <Save className="w-4 h-4" />
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNewsEditor;
